<script setup lang="ts">
import { computed, inject } from 'vue'
import { useField } from 'vee-validate'
import UiIcon from './UiIcon.vue'
import UiText from './UiText.vue'
import UiLockedBadge from './UiLockedBadge.vue'

type SupportedFieldType = 'text' | 'email' | 'password' | 'number' | 'url' | 'checkbox' | 'select' | 'combobox' | 'textarea' | 'phone' | 'multiselect-chips' | 'currency' | 'richtext' | 'file-upload' | 'avatar-upload' | 'date' | 'date-range'

export interface UiFormFieldProps<TValues extends Record<string, any>> {
  name: string
  /** Stable DOM id. Use this when the same field name is mounted more than once on a page. */
  id?: string
  label?: string
  labelCapitalize?: boolean
  inlineHint?: string
  /** 'end' right-aligns the hint (default); 'start' renders it inline after the label text. */
  inlineHintPlacement?: 'end' | 'start'
  invalid?: boolean
  type?: SupportedFieldType
  size?: 'md' | 'lg'
  locked?: boolean
  proBadge?: boolean
  disabled?: boolean
}

const props = withDefaults(defineProps<UiFormFieldProps<Record<string, any>>>(), {
  labelCapitalize: true,
  inlineHintPlacement: 'end',
  type: 'text',
  size: 'md',
})

const form = inject<any>('UiForm:form', null)

const { value, errorMessage, meta, handleChange, validate } = useField(props.name, undefined, {
  validateOnValueUpdate: false,
})

function onValidate() {
  if (!meta.dirty) return
  validate()
}

const fieldId = computed(() => props.id ?? (props.name as string).replace(/\.|\[/g, '-'))
const isInvalid = computed(() => Boolean(props.invalid || errorMessage.value))
const isDisabled = computed(() => Boolean(props.disabled || form?.meta?.isSubmitting))
</script>

<template>
  <div class="flex flex-col gap-1">
    <div v-if="props.label && props.type !== 'checkbox'" class="flex items-center gap-1">
        <!-- TODO: remove the icon or add a prop later -->
      <UiIcon name="lock" v-if="props.locked && false" class="w-5 h-5" />
      <UiText as="label" :for="fieldId" variant="h6" :class="[props.inlineHintPlacement === 'start' ? 'shrink-0' : 'flex-1', {
        'text-se-danger': isInvalid,
        'capitalize': props.labelCapitalize
      }]">{{ props.label }}</UiText>
      <UiText
        v-if="props.inlineHint && props.inlineHintPlacement === 'start' && !props.locked && !props.proBadge"
        as="span" variant="h6" class="text-se-gray-700" no-wrap
      >{{ props.inlineHint }}</UiText>
      <div class="flex items-center gap-1" :class="{ 'flex-1 justify-end': props.inlineHintPlacement === 'start' }">
        <UiLockedBadge v-if="props.locked || props.proBadge" />
        <UiText v-else-if="props.inlineHint && props.inlineHintPlacement === 'end'" as="span" variant="caption-normal" class="text-se-gray-700" no-wrap>{{ props.inlineHint }}</UiText>
      </div>
    </div>
      <slot 
        :modelValue="value"
        :onUpdateModelValue="(v:any)=> handleChange(v, !!errorMessage)"
        :onValidate="onValidate"
        :id="fieldId"
        :fieldName="props.name"
        :invalid="isInvalid"
        :error="errorMessage"
        :disabled="isDisabled"
        :meta="meta"
      />
  </div> 
</template>
